import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { enrollmentService, courseService, assessmentService } from '../api';
import {
    Book, Award, Compass, ChevronRight, BarChart2, TrendingUp, Users
} from 'lucide-react';
import { Link, Navigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import AdminDashboard from './Admin/AdminDashboard';
import ListEmptyState from '../components/list/ListEmptyState';
import { ListSection } from '../components/list/ShortCard';
import { EnrollmentShortCard } from '../components/list/ShortCardVariants';

function Dashboard() {
    const { user } = useAuth();
    const isFinanceOnly = user?.user_type === 'finance' && !user?.is_staff && !user?.is_superuser;
    const isAdmin = user?.is_staff || user?.is_superuser || ['admin', 'campus_admin', 'finance'].includes(user?.user_type);
    const [stats, setStats] = useState({
        totalCourses: 0,
        availableCourses: 0,
        pendingApprovals: 0,
        activeCourses: 0,
        certificates: 0,
        avgScore: '8.5/10'
    });
    const [recentCourses, setRecentCourses] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (isAdmin || isFinanceOnly) return;
        const fetchDashboardData = async () => {
            try {
                const enrollRes = await enrollmentService.getEnrollments();
                const myEnrollments = enrollRes.data.results || enrollRes.data;

                const assessRes = await assessmentService.getAssessments();
                const assessments = assessRes.data.results || assessRes.data;

                const courseRes = await courseService.getCourses();
                const available = courseRes.data.count || (courseRes.data.results || courseRes.data).length;

                setStats(prev => ({
                    ...prev,
                    totalCourses: myEnrollments.length,
                    availableCourses: available,
                    pendingApprovals: assessments.length,
                    certificates: myEnrollments.filter(e => e.status === 'completed').length
                }));

                setRecentCourses(myEnrollments.slice(0, 4));
            } catch (err) {
                console.error('Failed to fetch dashboard stats');
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, [isAdmin, isFinanceOnly]);

    if (isFinanceOnly) {
        return <Navigate to="/admin/enrollments/payment-verification" replace />;
    }

    if (isAdmin) {
        return <AdminDashboard />;
    }

    const statCards = [
        {
            key: 'enrollments',
            value: String(stats.totalCourses).padStart(2, '0'),
            label: 'Active Enrollments',
            icon: Book,
            iconClass: 'student-stat-icon-indigo',
            trend: '+2 this week',
            accent: 'stat-accent-cohorts',
        },
        {
            key: 'available',
            value: String(stats.availableCourses).padStart(2, '0'),
            label: 'Available Courses',
            icon: Compass,
            iconClass: 'student-stat-icon-blue',
            accent: 'stat-accent-completed',
        },
        {
            key: 'performance',
            value: stats.avgScore,
            label: 'Average Performance',
            icon: BarChart2,
            iconClass: 'student-stat-icon-purple',
            accent: 'stat-accent-pending',
        },
        {
            key: 'certificates',
            value: stats.certificates,
            label: 'Certificates Earned',
            icon: Award,
            iconClass: 'student-stat-icon-green',
            accent: 'stat-accent-approved',
        },
    ];

    return (
        <DashboardLayout>
            <div className="student-dashboard-header animate-slide-up">
                <div>
                    <h1 className="student-dashboard-title">
                        Welcome back, <span className="text-primary">{user?.first_name || 'Student'}</span>! 👋
                    </h1>
                    <p className="student-dashboard-subtitle">
                        Here&apos;s what&apos;s happening with your learning progress today.
                    </p>
                </div>
                <button type="button" className="btn btn-outline hover-lift">View Report</button>
            </div>

            <div className="stat-grid">
                {statCards.map((card, idx) => {
                    const Icon = card.icon;
                    return (
                        <div key={card.key} className={`stat-card hover-lift animate-slide-up stagger-${idx + 1} ${card.accent}`}>
                            <div className="flex items-center justify-between" style={{ marginBottom: '16px' }}>
                                <div className={`student-stat-icon ${card.iconClass}`}>
                                    <Icon size={24} />
                                </div>
                                {card.trend && (
                                    <span className="student-stat-trend">
                                        <TrendingUp size={16} /> {card.trend}
                                    </span>
                                )}
                            </div>
                            <div className="stat-value">{card.value}</div>
                            <div className="stat-label">{card.label}</div>
                        </div>
                    );
                })}
            </div>

            <div className="student-panel animate-slide-up stagger-3">
                <div className="student-panel-header">
                    <h3 className="student-panel-title">Recent Courses</h3>
                    <Link to="/courses" className="student-panel-link">
                        View All <ChevronRight size={18} />
                    </Link>
                </div>

                {recentCourses.length > 0 ? (
                    <ListSection title="Continue learning">
                        <div className="ux-short-scroll">
                            {recentCourses.map((enrollment, idx) => (
                                <EnrollmentShortCard key={enrollment.id} enrollment={enrollment} index={idx} />
                            ))}
                        </div>
                    </ListSection>
                ) : !loading && (
                    <ListEmptyState
                        icon={Book}
                        title="No active enrollments"
                        description="Start exploring courses to begin your learning journey."
                        action={(
                            <Link to="/courses" className="ux-btn-sm ux-btn-primary" style={{ textDecoration: 'none' }}>
                                Browse Catalogue
                            </Link>
                        )}
                    />
                )}
            </div>
        </DashboardLayout>
    );
}

export default Dashboard;
